import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { RoutePaths } from "../../../routes/routes";
import { useAppDispatch, useAppSelector } from "../../../store/redux-hooks";
import { useTransferCompanyOwnershipMutation } from "../../../store/slices/apiSlice";
import { setLoadedCompany } from "../../../store/slices/companySlice";
import { addAlert } from "../../../store/slices/notificationSlice";
import { transformCompanyResponseToLoadedCompany } from "../../../util/functions";

const CONFIRM_KEYWORD = "TRANSFER";

const DEFAULT_INPUT_VALUE = "";

export default ({
  show,
  toggleModal,
}: {
  show: boolean;
  toggleModal: () => void;
}) => {
  const [confirmValue, setConfirmValue] = useState(DEFAULT_INPUT_VALUE);

  const loadedCompany = useAppSelector((state) => state.company.loadedCompany);
  const companyUserData = useAppSelector(
    (state) => state.companyUsers.formData
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [
    transferOwnership,
    { isSuccess, isLoading, isUninitialized, data, reset: resetMutation },
  ] = useTransferCompanyOwnershipMutation();

  useEffect(() => {
    if (!show) {
      setConfirmValue(DEFAULT_INPUT_VALUE);
      resetMutation();
    }
  }, [show, resetMutation]);

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(setLoadedCompany(transformCompanyResponseToLoadedCompany(data)));
      dispatch(
        addAlert({
          variant: "success",
          forRoute: RoutePaths.USERS,
          message: `Ownership transferred to ${
            companyUserData.firstName || companyUserData.email
          }.`,
        })
      );
      navigate(RoutePaths.USERS);
    }
  }, [isSuccess, data, dispatch, navigate, companyUserData]);

  return (
    <Modal
      id="transfer-ownership-modal"
      show={show}
      onHide={toggleModal}
      centered
      className="custom-share-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Transfer company ownership</Modal.Title>
      </Modal.Header>
      <Form>
        <Modal.Body>
          <Container fluid>
            <Row className="mb-3 mt-3">
              <Col>
                You are about to transfer ownership of{" "}
                <strong>{loadedCompany?.name}</strong> to{" "}
                <strong>
                  {companyUserData.firstName || companyUserData.email}
                </strong>
                . After this you will no longer be the company owner and the
                new owner will be able to remove your account from the company.
                This action cannot be undone from within the app.
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                Type
                <span className="text-highlight-lightgray">
                  {` ${CONFIRM_KEYWORD} `}
                </span>
                below to confirm:
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group>
                  <Form.Control
                    type="text"
                    value={confirmValue}
                    placeholder={CONFIRM_KEYWORD}
                    onChange={({ target: { value } }) =>
                      setConfirmValue(value)
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Row className="justify-content-end m-0">
            {!isSuccess && (
              <Col xs="auto">
                <Button
                  type="submit"
                  variant="primary"
                  onClick={(event) => {
                    event.preventDefault();
                    if (loadedCompany?.id && companyUserData.userId) {
                      transferOwnership({
                        urlArgs: { companyId: loadedCompany.id },
                        body: { newOwnerId: companyUserData.userId },
                      });
                    }
                  }}
                  disabled={
                    isLoading ||
                    confirmValue.toUpperCase() !== CONFIRM_KEYWORD
                  }
                >
                  {isLoading ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    "Transfer Ownership"
                  )}
                </Button>
              </Col>
            )}
          </Row>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
